import type { Component } from 'vue'
import {
  BuildingOffice2Icon,
  ChartBarSquareIcon,
  ClipboardDocumentCheckIcon,
  Cog8ToothIcon,
  HomeIcon,
  LifebuoyIcon,
} from '@heroicons/vue/24/outline'
import type { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

type PermissionStore = ReturnType<typeof useBoostCorePermissionStore>

/**
 * Entrée du menu sidebar V2 (cf design-system/MASTER.md §10).
 *
 * Structure supportée (3 niveaux de données, 2 niveaux interactifs) :
 *   - niveau 1 : lien direct (icône obligatoire) OU hub accordéon (`children`)
 *   - niveau 2 : lien OU groupe repliable (`children`) — accordéon imbriqué
 *     accessible (bouton `aria-expanded`/`aria-controls`), replié par défaut
 *   - niveau 3 : liens uniquement
 *
 * Routes mixtes : `to` peut pointer vers une page V1 ou V2 — en mode v2 les deux
 * sont rendues dans le shell ApyLayout. Seules les routes `meta.version: 'v2'`
 * exigent que la BU de l'user ait activé le layout V2 (pref `layoutApy`, via le guard router).
 */
/**
 * Accent de section (niveau 1) — donne une couleur de marque à chaque hub pour
 * les distinguer d'un coup d'œil (cf charte APY). Mappé vers un jeton CSS dans
 * SidebarMenuList (`--apy-*`). La couleur ne véhicule jamais une info seule :
 * elle s'ajoute à l'icône + au libellé (MASTER §1).
 */
export type HubAccent = 'coral' | 'teal' | 'blue' | 'deep' | 'ardoise'

/**
 * Sous-thème par section : couleur (jeton CSS de marque) + couleur de texte
 * lisible au-dessus (AA) + libellé lisible (affiché dans le badge du header).
 * Source unique réutilisée par la sidebar, le header et le contenu, exposée en
 * CSS via `--hub-accent` (fond) et `--hub-accent-content` (texte par-dessus) —
 * cf ApyLayout. `content` permet un bouton/CTA teinté de la couleur de section
 * (ex. « Créer un ticket » corail dans le Hub Interne) tout en restant lisible.
 */
export const HUB_ACCENTS: Record<HubAccent, { color: string; content: string; label: string }> = {
  deep: { color: 'var(--apy-p1)', content: '#ffffff', label: 'Bleu pétrole' },
  teal: { color: 'var(--apy-p3)', content: '#082a36', label: "Vert d'eau" },
  blue: { color: 'var(--apy-p2)', content: '#ffffff', label: 'Indigo' },
  coral: { color: 'var(--apy-s2)', content: 'var(--apy-p1)', label: 'Corail' },
  ardoise: { color: 'var(--apy-slate)', content: '#ffffff', label: 'Ardoise' },
}

export interface SidebarMenuEntry {
  /** Clé stable — sert pour `expandedSections` (persisté) et les ids ARIA */
  id: string
  label: string
  /** Heroicon outline — requis au niveau 1 uniquement */
  icon?: Component
  /** Couleur d'accent du hub (niveau 1 uniquement) — distinction visuelle */
  accent?: HubAccent
  /** Path interne (V1 ou V2) — exclusif avec `href` */
  to?: string
  /** Lien externe — ouvre dans un nouvel onglet */
  href?: string
  /**
   * Lien externe dynamique, résolu via le store dans `filterSidebarMenu`
   * (ex: URL Odoo selon la source de l'user). Prioritaire sur `href`.
   */
  resolveHref?: (perms: PermissionStore) => string
  /** Visibilité selon permissions/flags (défaut : visible) */
  visible?: (perms: PermissionStore) => boolean
  children?: SidebarMenuEntry[]
}

export const sidebarMenu: SidebarMenuEntry[] = [
  {
    id: 'accueil',
    label: 'Accueil',
    icon: HomeIcon,
    accent: 'deep',
    to: '/accueil',
  },
  {
    id: 'mes-actions',
    label: 'Mes actions',
    icon: ClipboardDocumentCheckIcon,
    accent: 'coral',
    to: '/mes-actions',
  },
  {
    id: 'hub-client',
    label: 'Hub Client',
    icon: BuildingOffice2Icon,
    accent: 'teal',
    children: [
      // Page V2 dédiée : liste paginée + recherche client-only (ClientsListPage).
      { id: 'clients', label: 'Clients', to: '/clients' },
      // Page V2 dédiée : liste paginée + recherche contact-only (ContactsListPage).
      { id: 'contacts', label: 'Contacts', to: '/contacts' },
      { id: 'affaires', label: 'Affaires', to: '/opportunity' },
      {
        id: 'eligibilites',
        label: 'Éligibilités',
        to: '/eligibility',
        visible: (p) => p.canAccessEligibility,
      },
      // TODO V2 : "Factures" — pas de route interne aujourd'hui (Odoo), à trancher.
    ],
  },
  {
    id: 'mon-activite',
    label: 'Mon activité',
    icon: ChartBarSquareIcon,
    accent: 'blue',
    children: [
      {
        id: 'portefeuille-client',
        label: 'Portefeuille Client',
        to: '/commercial/dashboard',
        visible: (p) => p.can('SALES_READ'),
      },
      {
        // Demande d'accompagnement (front V2, API mockée). Visible dès ACCOMP_READ
        // (SUPER_ADMIN inclus). En DEV, le switcher de rôle injecte ACCOMP_*.
        id: 'accompagnement',
        label: 'Accompagnement',
        to: '/accompagnement',
        visible: (p) => p.can('ACCOMP_READ'),
      },
      {
        // Dépôt de factures BNP Factor (affacturage) — réservé DEV_ADMIN pour l'instant.
        id: 'factor-deposit',
        label: 'Dépôt BNP Factor',
        to: '/mon-activite/factor',
        visible: (p) => p.can('DEV_ADMIN'),
      },
      {
        id: 'commandes',
        label: 'Commandes',
        to: '/dashboard/orders',
        visible: (p) => p.canAccessOrders,
      },
      { id: 'kpi', label: 'KPI', to: '/kpi' },
      { id: 'agenda', label: 'Agenda', to: '/diary' },
      { id: 'todos', label: 'To Do', to: '/dashboard/todos' },
      { id: 'campagnes', label: 'Campagnes', to: '/campagnes' },
      {
        id: 'variables',
        label: 'Variables',
        to: '/variables',
        visible: (p) => p.canAccessVariables,
      },
      {
        // Marge par famille / semestre de l'équipe managée (récursif).
        // Réservé aux Sales Manager/Director ayant une équipe (rôle d'encadrement Sales + ≥1 subordonné).
        id: 'stats-familles',
        label: 'Stats familles',
        to: '/commercial/family-stats',
        visible: (p) => p.isManager && (p.include('SALES_MANAGER') || p.include('SALES_DIRECTOR')),
      },
    ],
  },
  {
    id: 'hub-interne',
    label: 'Hub Interne',
    icon: LifebuoyIcon,
    accent: 'coral',
    children: [
      { id: 'news', label: 'Nouveautés', to: '/news' },
      {
        id: 'leads',
        label: 'Leads',
        to: '/dashboard/leads',
        visible: (p) => p.canAccessLeads,
      },
      {
        id: 'tickets',
        label: 'Support interne',
        to: '/support-interne',
        // Parité V1 — à élargir quand le périmètre du support interne sera acté
        visible: (p) => p.include('SUPER_ADMIN'),
      },
      {
        // Stats d'appels H3A : outil interne (permission dédiée), pas de l'admin.
        id: 'h3a-stats',
        label: 'Stats H3A',
        to: '/h3a-stats',
        visible: (p) => p.can('H3A_STATS'),
      },
      {
        // Self-service : chacun consulte SON matériel (lecture seule).
        // Visible par tous les utilisateurs — aucune permission MATERIAL requise.
        id: 'mon-materiel',
        label: 'Mon matériel',
        to: '/mon-materiel',
      },
      {
        // Matériel collaborateur — cycle de vie complet (dotation à l'arrivée,
        // remplacements, restitution au départ). Accès MATERIAL_READ (ou DEV_ADMIN).
        id: 'onboarding',
        label: 'Matériel collaborateur',
        to: '/onboarding',
        visible: (p) => p.can('MATERIAL_READ') || p.can('DEV_ADMIN'),
      },
    ],
  },
  {
    // Miroir du dashboard admin (AdminHubPage `/dashboard/admin`) : mêmes groupes,
    // mêmes pages. Visibilité = guard de chaque route (= ce que l'user peut
    // réellement atteindre), pas la curation « pôle dev » du dashboard — ainsi un
    // DELIVERY_ADMIN garde son lien Livraisons, un SALES_READ voit la facturation, etc.
    id: 'admin',
    label: 'Admin',
    icon: Cog8ToothIcon,
    accent: 'ardoise',
    children: [
      // Vue d'ensemble : la page AdminHubPage qui regroupe toutes les cards admin.
      {
        id: 'admin-dashboard',
        label: "Tableau de bord",
        to: '/dashboard/admin',
        visible: (p) => p.include('SUPER_ADMIN'),
      },
      {
        id: 'admin-acces',
        label: 'Utilisateurs & accès',
        children: [
          {
            id: 'admin-users',
            label: 'Utilisateurs',
            to: '/admin/users',
            visible: (p) => p.include('USER_MASTER'),
          },
          {
            id: 'admin-access-control',
            label: 'Permissions & groupes',
            to: '/dashboard/admin/access-control',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-feature-flags',
            label: 'Feature flags',
            to: '/dashboard/admin/featureflag',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-organisation',
        label: 'Organisation',
        children: [
          {
            id: 'admin-business-units',
            label: 'Business Units',
            to: '/admin/business-units',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-regions',
            label: 'Régions',
            to: '/admin/regions',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-teams',
            label: 'Équipes',
            to: '/admin/teams',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-agencies',
            label: 'Agences',
            to: '/admin/agencies',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-reassign-clients',
            label: 'Réassignation clients',
            to: '/admin/reassign-clients',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-commercial',
        label: 'Commercial',
        children: [
          {
            id: 'admin-quotes-billing',
            label: 'Suivi facturation devis',
            to: '/dashboard/admin/quotes-billing',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-exports-devis',
            label: 'Export devis (lignes)',
            to: '/admin/exports-devis',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-plateforme',
        label: 'Plateforme',
        children: [
          {
            id: 'admin-banners',
            label: "Bandeaux d'information",
            to: '/dashboard/admin/banners',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-news',
            label: 'News & changelog',
            to: '/dashboard/admin/news',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-communication',
        label: 'Communication',
        children: [
          {
            id: 'admin-brevo-templates',
            label: 'Templates Brevo',
            to: '/dashboard/admin/brevo-templates',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-integrations',
        label: 'Intégrations',
        children: [
          {
            id: 'admin-livraisons',
            label: 'Livraisons',
            to: '/dashboard/admin/deliveries',
            visible: (p) => p.can('DELIVERY_ADMIN') || p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-sources',
            label: 'Sources',
            to: '/dashboard/admin/sources',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-imports-cegid',
            label: 'Imports CEGID',
            to: '/admin/imports-cegid',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-exports-cegid',
            label: 'Exports Cegid (TRA)',
            to: '/admin/exports-cegid',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-fiber-links',
            label: 'Liens fibre',
            to: '/admin/fiber-links',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            // Supervision des jobs planifiés Cronicle (dernier statut + lancement).
            id: 'admin-cronicle',
            label: 'Jobs Cronicle',
            to: '/admin/cronicle',
            visible: (p) => p.can('DEV_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-support',
        label: 'Support',
        children: [
          {
            id: 'admin-tickets',
            label: 'Tickets DEV',
            to: '/admin/tickets',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
          {
            id: 'admin-tickets-stats',
            label: 'Tickets DEV — stats',
            to: '/admin/tickets/stats',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
          {
            id: 'admin-pole-dev',
            label: 'Demandes pôle dev',
            to: '/admin/pole-dev',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
          {
            id: 'admin-pole-dev-communications',
            label: 'Communications pôle dev',
            to: '/admin/pole-dev/communications',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
          {
            id: 'admin-pole-dev-sprints',
            label: 'Sprints pôle dev',
            to: '/admin/pole-dev/sprints',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
          {
            id: 'admin-pole-dev-attention',
            label: "Points d'attention",
            to: '/admin/pole-dev/points-attention',
            visible: (p) => p.can('TICKETDEV_ADMIN'),
          },
        ],
      },
      {
        id: 'admin-espace-client',
        label: 'Espace client',
        children: [
          {
            id: 'admin-portal-permissions',
            label: 'Portal permissions',
            to: '/dashboard/admin/portal-permissions',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-espace-client-stats',
            label: 'Statistiques',
            to: '/dashboard/admin/espace-client/stats',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-feature-registry',
            label: 'Feature Registry',
            to: '/dashboard/admin/espace-client/feature-registry',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-faq',
            label: 'FAQ',
            to: '/dashboard/admin/espace-client/faq',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
          {
            id: 'admin-legal-pages',
            label: 'Pages légales',
            to: '/dashboard/admin/espace-client/legal-pages',
            visible: (p) => p.include('SUPER_ADMIN'),
          },
        ],
      },
    ],
  },
]

/**
 * Filtre récursivement le menu selon les permissions : un parent (hub ou groupe)
 * sans aucun enfant visible disparaît — un user sans droit admin ne voit pas
 * le hub Admin du tout.
 */
export function filterSidebarMenu(
  entries: SidebarMenuEntry[],
  perms: PermissionStore,
): SidebarMenuEntry[] {
  const result: SidebarMenuEntry[] = []
  for (const entry of entries) {
    if (entry.visible && !entry.visible(perms)) continue
    const resolved = entry.resolveHref ? { ...entry, href: entry.resolveHref(perms) } : entry
    if (!resolved.children) {
      result.push(resolved)
      continue
    }
    const children = filterSidebarMenu(resolved.children, perms)
    if (children.length > 0) result.push({ ...resolved, children })
  }
  return result
}

/** Id du hub de niveau 1 contenant le path actif (pour l'auto-dépliage). */
export function findActiveSectionId(
  entries: SidebarMenuEntry[],
  path: string,
): string | undefined {
  const containsPath = (entry: SidebarMenuEntry): boolean => {
    if (entry.to && (path === entry.to || path.startsWith(entry.to + '/'))) return true
    return entry.children?.some(containsPath) ?? false
  }
  return entries.find((e) => e.children && containsPath(e))?.id
}

/**
 * Ids de TOUS les conteneurs menant au path actif : hub niveau 1 + groupe niveau 2.
 * Sert à auto-déplier la chaîne complète (le groupe repliable contenant la page
 * active, pas seulement le hub).
 */
export function findActiveSectionPath(
  entries: SidebarMenuEntry[],
  path: string,
): string[] {
  const ids: string[] = []
  const walk = (list: SidebarMenuEntry[]): boolean => {
    for (const entry of list) {
      if (entry.to && (path === entry.to || path.startsWith(entry.to + '/'))) return true
      if (entry.children && walk(entry.children)) {
        ids.push(entry.id)
        return true
      }
    }
    return false
  }
  walk(entries)
  return ids
}

/**
 * Entrée de niveau 1 (hub OU lien direct comme Accueil) contenant le path actif.
 * Sert à appliquer le sous-thème de section (couleur du header, `--hub-accent`).
 */
export function findActiveHub(
  entries: SidebarMenuEntry[],
  path: string,
): SidebarMenuEntry | undefined {
  const contains = (entry: SidebarMenuEntry): boolean => {
    if (entry.to && (path === entry.to || path.startsWith(entry.to + '/'))) return true
    return entry.children?.some(contains) ?? false
  }
  return entries.find(contains)
}
